import { PhotoCredit } from "@/components/PhotoCredit";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car, Plus } from "lucide-react";
import { useGet } from "@/lib/api";
import { FAQ, WELCOME } from "@/lib/homeContent";

const TOP_TOWNS = ["norden", "greetsiel", "leer", "emden", "aurich", "norddeich", "carolinensiel", "neuharlingersiel", "wiesmoor"];
const LINKS = [["/inseln", "Inseln"], ["/orte", "Orte"], ["/veranstaltungen", "Veranstaltungen"], ["/gezeiten", "Gezeiten"], ["/karte", "Karte"], ["/entdecken/themen", "Themenwelten"]];

export const Welcome = () => (
  <section className="mx-auto grid max-w-[1400px] gap-8 px-5 pt-12 md:gap-10 md:px-10 md:pt-16 lg:grid-cols-12" data-testid="home-welcome">
    <div className="lg:col-span-4">
      <p className="eyebrow text-terracotta">Willkommen bei WattNu</p>
      <h2 className="mt-2 text-3xl tracking-tight md:text-4xl">Ostfriesland: Veranstaltungen, Ausflugsziele & Küste live</h2>
      <div className="mt-8 flex flex-wrap gap-2">
        {LINKS.map(([to, label]) => (
          <Link key={to} to={to} data-testid={`welcome-link-${label.toLowerCase()}`} className="lift inline-flex min-h-10 items-center border border-foreground/15 bg-white/50 px-3 py-2 font-sub text-sm hover:bg-white">{label}</Link>
        ))}
      </div>
    </div>
    <div className="text-base leading-relaxed text-foreground/80 lg:col-span-8 lg:columns-2 lg:gap-10">
      {WELCOME.map((t, i) => <p key={i} className={`mb-5 break-inside-avoid ${i === 0 ? "first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-6xl first-letter:leading-none first-letter:text-terracotta" : ""}`}>{t}</p>)}
    </div>
  </section>
);

export const Stats = () => {
  const { data } = useGet("home-stats", "/home/stats", {}, { staleTime: 3600_000 });
  const items = data ? [[data.events, "kommende Veranstaltungen"], [data.places, "Ausflugsziele & Orte"], [data.islands, "Inseln im Wattenmeer"], [data.towns, "Orte mit eigenem Guide"], [data.tours, "Rad- & Kanutouren"], [data.markets, "Wochenmärkte in 7 Tagen"]] : [];
  return (
    <section className="mt-12 bg-ocean text-shell md:mt-16" data-testid="home-stats">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px px-5 py-16 md:grid-cols-3 md:px-10 lg:grid-cols-6">
        {items.map(([n, label], i) => (
          <div key={label} className="rise py-4 pr-4" style={{ animationDelay: `${i * 80}ms` }} data-testid={`stat-${i}`}>
            <p className="font-display text-5xl leading-none md:text-6xl">{n.toLocaleString("de-DE")}</p>
            <p className="mt-3 font-sub text-xs uppercase tracking-wider text-shell/70">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const TownTiles = () => {
  const { data } = useGet("towns", "/towns", {});
  const towns = TOP_TOWNS.map((s) => data?.find((t) => t.slug === s)).filter(Boolean);
  if (!towns.length) return null;
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="home-towns">
      {towns.map((t, i) => (
        <div key={t.slug} className={`flex flex-col ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
        <Link to={`/orte/${t.slug}`} data-testid={`home-town-${t.slug}`}
          className={`group relative flex-1 overflow-hidden bg-ocean ${i === 0 ? "min-h-[380px]" : "min-h-[180px]"}`}>
          {t.image && <img src={t.image} alt={`${t.name} in Ostfriesland`} loading="lazy" className="img-tint absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            <h3 className={`font-display leading-tight ${i === 0 ? "text-4xl" : "text-2xl"}`}>{t.name}</h3>
            {i === 0 && <p className="mt-2 line-clamp-2 max-w-md text-sm text-white/80">{t.intro}</p>}
          </div>
        </Link>
        {t.image && <PhotoCredit credit={t.image_credit} className="mt-1.5" testid={`home-town-credit-${t.slug}`} />}
        </div>
      ))}
    </div>
  );
};

export const IslandStrip = () => {
  const { data } = useGet("islands", "/islands", {});
  if (!data?.length) return null;
  return (
    <div className="-mx-5 flex snap-x gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] md:mx-0 md:px-0 xl:grid xl:grid-cols-7 xl:overflow-visible" data-testid="home-islands">
      {data.map((isl, i) => (
        <div key={isl.slug} className="rise w-[220px] shrink-0 snap-start xl:w-auto" style={{ animationDelay: `${i * 70}ms` }}>
        <Link to={`/inseln/${isl.slug}`} data-testid={`home-island-${isl.slug}`}
          className="lift group relative block h-[340px] overflow-hidden bg-ocean">
          <img src={isl.image} alt={`Insel ${isl.name}`} loading="lazy" className="img-tint absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <span className="absolute left-4 top-4 font-display text-5xl text-white/40">{String(i + 1).padStart(2, "0")}</span>
          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
            <h3 className="font-display text-2xl">{isl.name}</h3>
            <p className="mt-1 line-clamp-2 text-xs text-white/80">{isl.tagline}</p>
            <p className="mt-2 flex items-center gap-1.5 font-sub text-[0.7rem] uppercase tracking-wider text-sand">
              <Car size={12} />{isl.car_free ? "autofrei" : "mit Auto"} · {isl.ferry?.harbor?.split(" ")[0]}
            </p>
          </div>
        </Link>
        <PhotoCredit credit={isl.image_credit} className="mt-1.5" testid={`home-island-credit-${isl.slug}`} />
        </div>
      ))}
    </div>
  );
};

export const Faq = () => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.faq = "home";
    el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
  return (
    <div className="divide-y divide-foreground/10 border-y border-foreground/10" data-testid="home-faq">
      {FAQ.map(([q, a], i) => (
        <details key={q} className="group py-5" data-testid={`faq-item-${i}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-display text-xl md:text-2xl [&::-webkit-details-marker]:hidden">
            {q}<Plus size={20} strokeWidth={1.5} className="shrink-0 text-terracotta transition-transform duration-300 group-open:rotate-45" />
          </summary>
          <p className="mt-3 max-w-3xl text-foreground/75">{a}</p>
        </details>
      ))}
      <Link to="/kontakt" data-testid="faq-contact-link" className="group flex items-center gap-2 py-5 font-sub text-sm hover:text-terracotta">Noch eine Frage? Schreib uns <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></Link>
    </div>
  );
};
